import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ConsumerGenerator } from '../components/_organisms/DesignerTabPane/GeneratorsTab/utils';
import backendService from '../services/backendService';
import { Collection } from '../services/backendService/types';

export const useEditCollection = (activeTenantId: string) => {
  const client = useQueryClient();

  const { mutate: createNewCollection, isLoading: isCreatingNewCollection } = useMutation(
    async ({
      collectionName,
    }: {
      collectionName: string;
      onSuccessFunction?: () => void | undefined;
    }) => {
      const res = await backendService.instance.collection.createNewCollection(
        activeTenantId,
        collectionName,
      );
      return res;
    },
    {
      //onSuccess refetches the data from the server and closes the modal
      onSuccess: (data, { onSuccessFunction }) => {
        if (onSuccessFunction && data) {
          onSuccessFunction();
        }
        client.invalidateQueries(['collections', activeTenantId]);
      },
    },
  );
  const { mutate: renameCollection, isLoading: isRenamingCollection } = useMutation(
    async ({
      collectionName,
      collectionId,
    }: {
      collectionName: string;
      collectionId: string;
      onSuccessFunction?: () => void | undefined;
    }) => {
      const res = await backendService.instance.collection.renameCollection(
        activeTenantId,
        collectionName,
        collectionId,
      );
      return res;
    },
    {
      //onSuccess refetches the data from the server and closes the modal
      onSuccess: (data, { onSuccessFunction }) => {
        if (onSuccessFunction && data) {
          onSuccessFunction();
        }
        client.invalidateQueries(['collections', activeTenantId]);
      },
    },
  );

  const { mutate: deleteCollection, isLoading: isDeletingCollection } = useMutation(
    async ({
      collectionId,
    }: {
      collectionId: string;
      onSuccessFunction?: () => void | undefined;
    }) => {
      const res = await backendService.instance.collection.deleteCollection(
        activeTenantId,
        collectionId,
      );
      return res;
    },
    {
      //onSuccess refetches the data from the server and closes the modal
      onSuccess: (data, { onSuccessFunction }) => {
        if (onSuccessFunction && data) {
          onSuccessFunction();
        }
        client.invalidateQueries(['collections', activeTenantId]);
      },
    },
  );

  const {
    mutate: saveGeneratorToCollection,
    isLoading: isSavingGeneratorToCollection,
  } = useMutation(
    async ({
      collectionId,
      generator,
    }: {
      collectionId: string;
      generator: ConsumerGenerator;
      onSuccessFunction?: (data: string) => void | undefined;
    }) => {
      const res = await backendService.instance.collection.addGeneratorToCollection(
        activeTenantId,
        collectionId,
        generator.id,
      );
      return res;
    },
    {
      onMutate: async ({ collectionId, generator }) => {
        await client.cancelQueries(['collections', activeTenantId]);
        await client.cancelQueries(['public-collected-generators', collectionId]);
        await client.cancelQueries(['collected-generators', collectionId]);
        const previousCollections = client.getQueryData(['collections', activeTenantId]);

        //optimistically update the collection cache
        client.setQueryData<Collection[]>(
          ['collections', activeTenantId],
          oldCollections => {
            const newCollection = oldCollections?.map(collection => {
              if (collection.collectionId === collectionId) {
                return {
                  ...collection,
                  collectedGeneratorIds: [
                    ...collection.collectedGeneratorIds,
                    generator.id,
                  ],
                };
              }
              return collection;
            });
            return newCollection;
          },
        );

        //optimistically update the public collected generators cache
        const previousData = client.getQueryData([
          'public-collected-generators',
          collectionId,
        ]);

        // Optimistically add the generator to the cached pages
        client.setQueryData(
          ['public-collected-generators', collectionId],
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              pages: oldData.pages.map((page: any) => {
                return {
                  ...page,
                  items: [
                    ...page.items,
                    {
                      id: generator.id,
                    },
                  ],
                };
              }),
            };
          },
        );

        //optimistically update the collected generators cache
        const previousCollectedGenerators = client.getQueryData([
          'collected-generators',
          collectionId,
        ]);

        // Optimistically add the entire generator to the cached collected generators
        client.setQueryData(['collected-generators', collectionId], (oldData: any) => {
          if (!oldData) return oldData;
          return [...oldData, generator];
        });

        return { previousCollections, previousData, previousCollectedGenerators };
      },
      onSuccess: (data, { collectionId }) => {
        //refetch the collections and collected generators if its your first collection
        if (collectionId === 'my-favorites') {
          client.invalidateQueries(['collections', activeTenantId]);
          client.invalidateQueries(['public-collected-generators', data.collectionId]);
          client.invalidateQueries(['collected-generators', data.collectionId]);
        }
      },
      onError: (error, variables, context) => {
        //rollback the cache
        if ((context as { previousCollections?: Collection[] })?.previousCollections) {
          client.setQueryData(
            ['collections', activeTenantId],
            (context as { previousCollections?: Collection[] }).previousCollections,
          );
        }
      },
    },
  );
  const {
    mutate: removeGeneratorFromCollection,
    isLoading: isRemovingGeneratorToCollection,
  } = useMutation(
    async ({
      collectionId,
      generatorId,
    }: {
      collectionId: string;
      generatorId: string;
      onSuccessFunction?: (data: string) => void | undefined;
    }) => {
      return await backendService.instance.collection.removeGeneratorFromCollection(
        activeTenantId,
        collectionId,
        generatorId,
      );
    },
    {
      onMutate: async ({ collectionId, generatorId }) => {
        await client.cancelQueries(['collections', activeTenantId]);
        await client.cancelQueries(['public-collected-generators', collectionId]);
        const previousCollections = client.getQueryData(['collections', activeTenantId]);

        //optimistically update the collection cache
        client.setQueryData<Collection[]>(
          ['collections', activeTenantId],
          oldCollections => {
            const newCollection = oldCollections?.map(collection => {
              if (collection.collectionId === collectionId) {
                return {
                  ...collection,
                  collectedGeneratorIds: collection.collectedGeneratorIds.filter(
                    id => id !== generatorId,
                  ),
                };
              }
              return collection;
            });
            return newCollection;
          },
        );

        //optimistically update the public collected generators cache
        const previousData = client.getQueryData([
          'public-collected-generators',
          collectionId,
        ]);

        // Optimistically remove the generator from the cached pages
        client.setQueryData(
          ['public-collected-generators', collectionId],
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              pages: oldData.pages.map((page: any) => {
                return {
                  ...page,
                  items: page.items.filter(
                    (item: ConsumerGenerator) => item.id !== generatorId,
                  ),
                };
              }),
            };
          },
        );

        //optimistically update the collected generators cache
        const previousCollectedGenerators = client.getQueryData([
          'collected-generators',
          collectionId,
        ]);

        // Optimistically remove the generator from the cached collected generators
        client.setQueryData(['collected-generators', collectionId], (oldData: any) => {
          if (!oldData) return oldData;
          return oldData.filter((item: ConsumerGenerator) => item.id !== generatorId);
        });

        return { previousCollections, previousData, previousCollectedGenerators };
      },
      onError: (error, variables, context) => {
        //rollback the cache
        if ((context as { previousCollections?: Collection[] })?.previousCollections) {
          client.setQueryData(
            ['collections', activeTenantId],
            (context as { previousCollections?: Collection[] }).previousCollections,
          );
        }
      },
    },
  );

  return {
    createNewCollection,
    renameCollection,
    deleteCollection,
    saveGeneratorToCollection,
    removeGeneratorFromCollection,
    isCreatingNewCollection,
    isRenamingCollection,
    isDeletingCollection,
    isSavingGeneratorToCollection,
    isRemovingGeneratorToCollection,
  };
};
